import type { IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type {
  Account,
  Billable,
  Event,
  FacebookCampaign,
  FacebookDisplayCreative,
  Goal,
  IFacebook,
  Segment,
  Targeting,
  User,
} from '@feathr/blackbox';
import { CardV2 as Card, Form } from '@feathr/components';
import { useBlackboxVersion } from '@feathr/extender/hooks';
import { useFlags, useStore, useUser } from '@feathr/extender/state';

import type { ICampaignValidationErrors } from '../../../CampaignSummary';
import CampaignSummary from '../../../CampaignSummary';
import type { IUniqueEmailsResult } from '../../AdWizard/AddWizard.useReconcileUniqueEmails';
import { useReconcileUniqueEmails } from '../../AdWizard/AddWizard.useReconcileUniqueEmails';
import { getTargetSegments } from '../../AdWizard/AdWizardTargetsStep';
import { getGoalSegments } from '../../StepGoals';

import * as styles from '../../AdWizard/AdWizard.css';

interface ICampaignDetails {
  campaign: FacebookCampaign;
  targetings: IObservableArray<Targeting>;
  targetingSegments?: Segment[];
  goals: IObservableArray<Goal>;
  goalSegments?: Segment[];
  creatives: IObservableArray<FacebookDisplayCreative>;
  billable?: Billable;
  account: Account;
  event: Event;
  integration?: IFacebook;
  emailValidation: IUniqueEmailsResult;
}

interface IProps {
  campaign: FacebookCampaign;
  targets: IObservableArray<Targeting>;
  goals: IObservableArray<Goal>;
  creatives: IObservableArray<FacebookDisplayCreative>;
  billable?: Billable;
  account: Account;
  event: Event;
  validate: (
    details: ICampaignDetails,
    isOnProduction: boolean,
    user?: User,
    noMaxBudget?: boolean,
  ) => ICampaignValidationErrors;
  integration?: IFacebook;
}

function FacebookReviewStep({
  campaign,
  targets,
  goals,
  creatives,
  billable,
  account,
  event,
  validate,
  integration,
}: IProps): JSX.Element {
  const { t } = useTranslation();
  const { Segments } = useStore();
  const flags = useFlags();
  const user = useUser();
  const { isOnProduction } = useBlackboxVersion();

  const targetingSegments = getTargetSegments(targets, Segments);
  const goalSegments = getGoalSegments(goals, Segments);
  const emailValidation = useReconcileUniqueEmails({
    targetings: targets,
    campaign,
  });

  const errors = validate(
    {
      account,
      billable,
      campaign,
      creatives,
      event,
      goals,
      goalSegments,
      integration,
      targetings: targets,
      targetingSegments,
      emailValidation,
    },
    isOnProduction,
    user,
    flags.noMaxBudget,
  );

  return (
    <Form className={styles.formRoot} label={'Edit Campaign: Review'}>
      <Card>
        <Card.Header
          description={t(
            "Check the summary below to make sure everything is configured correctly. When you're ready, click Publish to launch your campaign!",
          )}
          title={'Review'}
        />
        <Card.Content>
          <CampaignSummary
            campaign={campaign}
            creatives={creatives}
            layout={'vertical'}
            validationErrors={errors}
          />
        </Card.Content>
      </Card>
    </Form>
  );
}

export default observer(FacebookReviewStep);
